/**
 * Project analysis (lot L2). Mirrors backend app/schemas/projects/analysis.py
 * and app/schemas/knowledge_engine/ai_prompts.py.
 *
 * The analysis content is stored as loose JSON server-side: every field is
 * optional here and read through `normalizeAnalysisContent`.
 */

/* --------------------------------- runs ---------------------------------- */

export type AnalysisRunStatus = 'queued' | 'running' | 'succeeded' | 'failed';
export const ANALYSIS_PHASES = ['extracting', 'matching', 'retrieving', 'analysing'] as const;
export type AnalysisPhase = (typeof ANALYSIS_PHASES)[number];

export interface AnalysisRun {
  id: string;
  project_id: string;
  project_title?: string | null;
  status: AnalysisRunStatus;
  phase?: AnalysisPhase | null;
  error?: string | null;
  analysis_id?: string | null;
  created_at: string;
  started_at?: string | null;
  finished_at?: string | null;
}

export const isRunActive = (run: Pick<AnalysisRun, 'status'>): boolean =>
  run.status === 'queued' || run.status === 'running';

/* ------------------------------- content --------------------------------- */

export type FactSource = 'file' | 'answer' | 'kb' | 'deduced';
export const FACT_KEYS = ['objet', 'pourQui', 'perimetre', 'donnees', 'rythme'] as const;
export type FactKey = (typeof FACT_KEYS)[number];
/** The facts shown as cards; « périmètre » stays in the data (markdown, prompts) but is not displayed. */
export const DISPLAYED_FACT_KEYS: readonly FactKey[] = ['objet', 'pourQui', 'donnees', 'rythme'];
export type SourceKey = FactKey | 'understood';
export type Severity = 'low' | 'med' | 'high';

export interface AnalysisRef {
  id?: string | null;
  label: string;
}

export interface RelatedProject {
  projectId?: string | null;
  title: string;
  reason?: string;
}

export interface AnalysisPlacement {
  domainId?: string | null;
  domain?: string;
  text?: string;
  attaches?: AnalysisRef[];
}

export interface ReusableItem extends AnalysisRef {
  what?: string;
  how?: string;
}

export interface CarrierItem extends AnalysisRef {
  role?: string;
  why?: string;
}

export interface ExistingTechnology extends AnalysisRef {
  usage?: string;
}

export interface NewTechnology {
  label: string;
  why?: string;
}

export interface VigilancePoint {
  title: string;
  detail?: string;
  severity: Severity;
}

export interface ActionStep {
  step: string;
  who?: string;
  resource?: string;
}

export interface AnalysisCitation {
  n: number;
  chunkId?: string | null;
  documentId?: number | null;
  documentName?: string | null;
  excerpt?: string;
}

export interface AnalysisDepositTrace {
  total: number;
  selected: string[];
  reason?: string;
}

export interface ProjectAnalysisContent {
  understood?: string;
  facts?: Partial<Record<FactKey, string>>;
  sources?: Partial<Record<SourceKey, FactSource>>;
  related?: RelatedProject[];
  placement?: AnalysisPlacement;
  reusable?: ReusableItem[];
  carriers?: CarrierItem[];
  technologies?: { existing?: ExistingTechnology[]; toIntroduce?: NewTechnology[] };
  vigilance?: VigilancePoint[];
  synthesis?: { summary?: string; actions?: ActionStep[] };
  citations?: AnalysisCitation[];
  deposit?: AnalysisDepositTrace | null;
  generatedAt?: string | null;
}

/** Content with every collection present (what the views render). */
export interface NormalizedAnalysisContent {
  understood: string;
  facts: Record<FactKey, string>;
  sources: Partial<Record<SourceKey, FactSource>>;
  related: RelatedProject[];
  placement: { domainId: string | null; domain: string; text: string; attaches: AnalysisRef[] };
  reusable: ReusableItem[];
  carriers: CarrierItem[];
  technologies: { existing: ExistingTechnology[]; toIntroduce: NewTechnology[] };
  vigilance: VigilancePoint[];
  synthesis: { summary: string; actions: ActionStep[] };
  citations: AnalysisCitation[];
  deposit: AnalysisDepositTrace | null;
  generatedAt: string | null;
}

export interface ProjectAnalysis {
  id: string;
  project_id: string;
  version: number;
  deposit_version: number;
  /** The deposit changed since this analysis (new version or added files). */
  stale: boolean;
  content: ProjectAnalysisContent;
  provider?: string | null;
  model?: string | null;
  is_current: boolean;
  created_at: string;
}

export interface ProjectAnalysisSummary {
  id: string;
  version: number;
  deposit_version: number;
  is_current: boolean;
  model?: string | null;
  created_at: string;
}

/* ----------------------------- micro-actions ----------------------------- */

export const MICRO_CATEGORIES = ['business', 'technique', 'conformite'] as const;
export type MicroCategory = (typeof MICRO_CATEGORIES)[number];
export type MicroKind = 'confirm' | 'question';
export type MicroSource = 'file' | 'ontology' | 'kb' | 'deduced';

export interface MicroAction {
  id: string;
  category: MicroCategory;
  kind: MicroKind;
  text: string;
  source?: MicroSource | null;
  topic?: string | null;
  title?: string | null;
  why?: string | null;
  options: string[];
  answer_value?: string | null;
  answer_confirmed?: boolean | null;
  answered_at?: string | null;
  answered_by?: string | null;
}

/** An answer exists once it has been timestamped by the backend. */
export const isAnswered = (m: MicroAction): boolean => !!m.answered_at;

export const isMicroCategory = (v: unknown): v is MicroCategory =>
  typeof v === 'string' && (MICRO_CATEGORIES as readonly string[]).includes(v);

/** What is still to clarify: the distinct titles (or topics) of the unanswered micro-actions. */
export const missingTopics = (list: MicroAction[]): string[] => [
  ...new Set(
    list
      .filter((m) => !isAnswered(m))
      .map((m) => (m.title || m.topic || '').trim())
      .filter(Boolean)
  ),
];

export interface MicroActionAnswerPayload {
  value: string;
  /** confirm only: true = "C'est ça", false = "Non". */
  confirmed?: boolean;
}

/* -------------------------------- prompts -------------------------------- */

export const PROJECT_PROMPT_KEYS = [
  'project.file_selection',
  'project.concepts',
  'project.analysis.system',
  'project.analysis.user',
  'project.reanalysis',
  'project.artifact_context',
  'project.artifact_welcome',
  // Portfolio (lot L4): names the map's motifs, finds the rapprochements, writes the insight.
  'portfolio.map_naming',
] as const;
export type ProjectPromptKey = (typeof PROJECT_PROMPT_KEYS)[number];
export type PromptLocale = 'fr' | 'en';

export interface AiPromptVersion {
  locale: PromptLocale;
  default: string;
  override?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface AiPrompt {
  key: string;
  placeholders: string[];
  versions: AiPromptVersion[];
}

/* -------------------------------- limits --------------------------------- */

export const ANALYSIS_LIMIT_KEYS = [
  'max_chars_per_file',
  'max_chars_per_deposit',
  'max_ontology_lines',
  'max_rag_chunks',
  'max_peer_projects',
  'max_artifact_context_tokens',
] as const;
export type AnalysisLimitKey = (typeof ANALYSIS_LIMIT_KEYS)[number];
export type AnalysisLimitValues = Partial<Record<string, number>>;

export interface AnalysisLimits {
  defaults: Record<string, number>;
  bounds: Record<string, [number, number] | number[]>;
  global_values: Record<string, number>;
  organization_values: Record<string, number>;
  effective: Record<string, number>;
  organization_id?: string | null;
  can_edit: boolean;
}

/* ------------------------------ normalising ------------------------------ */

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const ref = (v: unknown): AnalysisRef | null => {
  if (!v || typeof v !== 'object') return null;
  const o = v as { id?: unknown; label?: unknown };
  const label = str(o.label).trim();
  const id = typeof o.id === 'string' && o.id.trim() ? o.id.trim() : null;
  return label || id ? { id, label: label || (id ?? '') } : null;
};

const SEVERITIES: readonly Severity[] = ['low', 'med', 'high'];

/** Defensive read of the stored JSON: missing collections become empty. */
export function normalizeAnalysisContent(raw: ProjectAnalysisContent | null | undefined): NormalizedAnalysisContent {
  const c = raw ?? {};
  const facts = Object.fromEntries(FACT_KEYS.map((k) => [k, str(c.facts?.[k])])) as Record<FactKey, string>;
  const placement = c.placement ?? {};
  const deposit = c.deposit && typeof c.deposit === 'object' ? c.deposit : null;
  return {
    understood: str(c.understood),
    facts,
    sources: c.sources ?? {},
    related: arr<RelatedProject>(c.related).filter((r) => !!str(r?.title)),
    placement: {
      domainId: placement.domainId ?? null,
      domain: str(placement.domain),
      text: str(placement.text),
      attaches: arr<unknown>(placement.attaches).map(ref).filter((r): r is AnalysisRef => !!r),
    },
    reusable: arr<ReusableItem>(c.reusable).filter((r) => !!ref(r)),
    carriers: arr<CarrierItem>(c.carriers).filter((r) => !!ref(r)),
    technologies: {
      existing: arr<ExistingTechnology>(c.technologies?.existing).filter((r) => !!ref(r)),
      toIntroduce: arr<NewTechnology>(c.technologies?.toIntroduce).filter((r) => !!str(r?.label)),
    },
    vigilance: arr<VigilancePoint>(c.vigilance)
      .filter((v) => !!v && typeof v === 'object')
      .map((v) => ({
        title: str(v.title) || '—',
        detail: str(v.detail),
        severity: SEVERITIES.includes(v.severity) ? v.severity : 'med',
      })),
    synthesis: {
      summary: str(c.synthesis?.summary),
      actions: arr<ActionStep>(c.synthesis?.actions).filter((a) => !!str(a?.step)),
    },
    citations: arr<AnalysisCitation>(c.citations).filter((x) => typeof x?.n === 'number'),
    deposit: deposit
      ? { total: Number(deposit.total) || 0, selected: arr<string>(deposit.selected).map(str), reason: str(deposit.reason) }
      : null,
    generatedAt: c.generatedAt ?? null,
  };
}
